import { fromJS, List } from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';
import { valuePassesIsTypeOperatorKeyCheck } from 'flow/actions/SupervisorActions';
import { ActiveLineData } from 'flow/types/ActiveLineData';
import { ExtensionKey, QueueKey, TicketKey } from 'flow/types/EntityKeys';
import { OperatorMember } from 'flow/types/Operator';
import { State } from 'flow/types/State';
import {
    AgentMinimum,
    QueueMinimumWithFavoriteInfo,
} from 'flow/types/Supervisor';
import { TypedMap } from 'flow/types/TypedMap';
import { listToMap } from 'flow/utils';
import { TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE } from './call-selectors';
import {
    selectExtensions,
    selectNextScheduledProfiles,
} from './ExtensionSelectors';
import { selectQueueFavorites } from './favorite-selectors';
import { selectActiveMeetingCallId } from './meeting-selectors';
import { selectOperatorData } from './operator-selectors';
import { selectQueueDescription, selectQueueMembers } from './queue-selectors';
import { getShouldUseTwoHourSoonLoggedInThreshold } from './selectors';

const LIST_EMPTY = List();
const TEN_MINUTES = 1000 * 60 * 10;
const TWO_HOURS = 1000 * 60 * 120;

const selectExtensionsActiveProfiles = (state: State) =>
    state.extensions.get('activeProfiles');
// Needs to be memoized when used.
// Read info in links below:
// https://redux.js.org/usage/deriving-data-selectors#selector-factories
// https://redux.js.org/usage/deriving-data-selectors#creating-unique-selector-instances
export const makeSelectQueueMembersSoonLoggedIn = () => {
    return createSelector(
        [
            selectExtensionsActiveProfiles,
            selectNextScheduledProfiles,
            selectQueueMembers,
            selectQueueMembersSoonLoggedInThreshold,
            (_state: State, queueEntityKey: QueueKey) => queueEntityKey,
        ],
        (
            extensionsActiveProfiles,
            nextScheduledProfiles,
            queueMembers,
            soonLoggedInThreshold,
            queueEntityKey,
        ) => {
            const membersSoonLoggedIn: TypedMap<MemberSoonLoggedIn>[] = [];
            queueMembers.forEach((member) => {
                if (member.get('loggedIn')) {
                    //Queue member already logged in to queue; not adding to list returned.
                    return;
                }

                const extensionKey = member.get('extensionKey');

                const nextScheduledProfile =
                    nextScheduledProfiles?.get(extensionKey);

                if (!nextScheduledProfile?.get('available')) {
                    return;
                }

                const activeProfileUntil = extensionsActiveProfiles?.getIn([
                    extensionKey,
                    'activeUntil',
                ]);

                if (
                    !activeUntilIsWithinThreshold(
                        activeProfileUntil,
                        soonLoggedInThreshold,
                    )
                ) {
                    return;
                }

                const queueLoginList =
                    nextScheduledProfile.get('queueLoginList');
                if (
                    queueLoginList?.find(
                        (queueDTO) =>
                            queueDTO.get('queueEntityKey') == queueEntityKey &&
                            queueDTO.get('loggedIn'),
                    )
                ) {
                    const soonLoggedInMemberItem = fromJS({
                        extensionKey: extensionKey,
                        currentProfileActiveUntil: activeProfileUntil,
                    });
                    membersSoonLoggedIn.push(soonLoggedInMemberItem);
                }
            });
            return List(membersSoonLoggedIn);
        },
    );
};

const activeUntilIsWithinThreshold = (
    activeUntil: string,
    threshold: number,
) => {
    if (!activeUntil) return undefined;

    const now = moment();
    const timeBetweenNowAndActiveUntil = moment
        .duration(moment(new Date(activeUntil)).diff(now))
        .asMilliseconds();

    //Only present countdown for profiles if the time slot will occur within the threshold
    return threshold >= timeBetweenNowAndActiveUntil;
};

interface MemberSoonLoggedIn {
    extensionKey: ExtensionKey;
    currentProfileActiveUntil: string;
}

export const selectQueueMembersSoonLoggedInThreshold = createSelector(
    [getShouldUseTwoHourSoonLoggedInThreshold],
    (shouldUseTwoHourThreshold) => {
        return shouldUseTwoHourThreshold ? TWO_HOURS : TEN_MINUTES;
    },
);

export const selectSelectedQueue = (state: State) =>
    state.supervisor.getIn(['selected', 'queue']);

export const selectSelectedQueueDescription = (state: State) => {
    const key = selectSelectedQueue(state);
    if (key === 'all') return key;
    return selectQueueDescription(state, key);
};

export const selectSelectedOperator = (state: State) =>
    state.supervisor.getIn(['selected', 'operatorGroup']);

const selectQueuesSortingData = (state: State) =>
    state.supervisor.getIn(['sortingData', 'queuesMinimum']);

const selectQueuesMinimumSortedByFavoriteAndDescription = createSelector(
    [selectQueuesSortingData, selectQueueFavorites],
    (queuesMinimum, queueFavorites) =>
        queueFavorites
            ?.reduce(
                (
                    acc: TypedMap<
                        Record<
                            TicketKey,
                            TypedMap<QueueMinimumWithFavoriteInfo>
                        >
                    >,
                    favoriteItem,
                ) => {
                    const queueKey = favoriteItem.getIn([
                        'favoriteData',
                        'key',
                    ]);
                    return acc.has(queueKey)
                        ? acc.setIn([queueKey, 'isFavorite'], true)
                        : acc;
                },
                queuesMinimum,
            )
            ?.sort(sortQueuesComparator),
);

const selectAgentsMinimum = (state: State) =>
    state.supervisor.getIn(['sortingData', 'agentsMinimum']);

const selectAgentsMinimumSortedByName = createSelector(
    [selectAgentsMinimum],
    (agentsMinimum) => agentsMinimum?.sort(sortAgentsComparator),
);

const selectAgentsMinimumPerQueue = (state: State) =>
    state.supervisor.getIn(['sortingData', 'agentsMinimumPerQueue']);

//This is a map with objects containing only the bare minimum required for sorting
export const selectSelectedOperatorMembers = createSelector(
    [selectOperatorData, selectSelectedOperator],
    (operators, selectedOperatorKey) => {
        if (!valuePassesIsTypeOperatorKeyCheck(selectedOperatorKey)) {
            return undefined;
        }

        const selectedOperatorMembers = operators
            ?.get(selectedOperatorKey)
            ?.get('members');

        if (!selectedOperatorMembers) {
            return undefined;
        }

        return listToMap(
            selectedOperatorMembers,
            (member: TypedMap<OperatorMember>) => member.get('key'),
        );
    },
);

//This is a map with objects containing only the bare minimum required for sorting
export const selectAgentsSortingDataInSelectedOperatorGroup = createSelector(
    [
        selectSelectedOperatorMembers,
        selectAgentsMinimumSortedByName,
        selectAgentsMinimumPerQueue,
        selectSelectedQueue,
    ],
    (operatorMembers, agentsMinimum, agentsMinimumPerQueue, selectedQueue) => {
        if (selectedQueue === 'all') {
            if (!operatorMembers) return agentsMinimum;
            return agentsMinimum?.filter((agent) =>
                operatorMembers.has(agent.get('extensionKey')),
            );
        }

        return agentsMinimumPerQueue
            ?.get(selectedQueue)
            ?.sort(sortAgentsComparator);
    },
);

export const selectSelectedOperatorQueuesSortingData = createSelector(
    [
        selectSelectedOperatorMembers,
        selectQueuesMinimumSortedByFavoriteAndDescription,
    ],
    (operatorMembers, queuesMinimum) => {
        if (!operatorMembers) return queuesMinimum;
        return queuesMinimum?.filter((queue) =>
            operatorMembers.has(queue.get('key')),
        );
    },
);

const sortQueuesComparator = (
    a: TypedMap<QueueMinimumWithFavoriteInfo>,
    b: TypedMap<QueueMinimumWithFavoriteInfo>,
) => {
    const isFavoriteA = a?.get('isFavorite') === true ? 0 : 1;
    const isFavoriteB = b?.get('isFavorite') === true ? 0 : 1;

    if (isFavoriteA !== isFavoriteB) {
        return isFavoriteA - isFavoriteB;
    }

    const aName = a?.get('description');
    const bName = b?.get('description');
    return aName < bName ? -1 : 1;
};

const sortAgentsComparator = (
    a: TypedMap<AgentMinimum>,
    b: TypedMap<AgentMinimum>,
) => {
    const aName = getAgentFullName(a);
    const bName = getAgentFullName(b);
    return aName < bName ? -1 : 1;
};

const getAgentFullName = (agent?: TypedMap<AgentMinimum>) =>
    `${agent?.get('firstName')} ${agent?.get('lastName')}`;

const selectActiveLineData = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['activeLineData', key]);

export const makeSelectActiveLineData = () => {
    return createSelector(
        [
            selectActiveLineData,
            selectActiveMeetingCallId,
            TREAT_CENTION_ERRANDS_LIKE_PHONE_CALLS_EXPERIMENTAL_FEATURE,
        ],
        (
            activeLineData = LIST_EMPTY as List<TypedMap<ActiveLineData>>,
            activeMeetingCallId,
            includeCentionErrands,
        ) => {
            if (includeCentionErrands) {
                return activeLineData.filter(
                    (item) => item.get('callId') !== activeMeetingCallId,
                );
            } else {
                return activeLineData.filter(
                    (item) =>
                        !item.get('isOmnichannelErrand') &&
                        item.get('callId') !== activeMeetingCallId,
                );
            }
        },
    );
};
